import React, { useDeferredValue, useEffect, useMemo, useState, useTransition } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { useWarbandStore } from '../../stores/warbandStore';
import { formatSoldierType } from '../../utils/formatting';
import { SpellCard } from '../shared/SpellCard';
import { SpellCardMini } from '../shared/SpellCardMini';
import { CreatureCard } from '../shared/CreatureCard';
import { CreatureCardMini } from '../shared/CreatureCardMini';
import { createColouredGlassSurface, createIceSurface, createParchmentLabelSurface, createParchmentSurface, createSubtleIceSurface, hashStringToSeed, parchmentUnderlineSx } from '../../theme/surfaces';
import { gridFlexSx, miniFlexSx } from '../../App';
import { useGameStore } from '../../stores/gameStore';
import { getGameDefinition } from '../../game/gameDefinitions';
import { IndexGroupHeader, IndexLayout, IndexSectionHeader } from '../shared/IndexLayout';
import {
  buildFrostgraveIndexEntries,
  filterFrostgraveIndex,
  FrostgraveIndexCategory,
  FrostgraveReferenceEntry,
} from '../../game/frostgraveIndex';

const categories: Array<{ value: FrostgraveIndexCategory | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'spells', label: 'Spells' },
  { value: 'bestiary', label: 'Bestiary' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'items', label: 'Magic Items' },
  { value: 'bases', label: 'Bases' },
];

const sectionLabels: Record<Exclude<FrostgraveIndexCategory, 'spells'>, string> = {
  bestiary: 'Creatures',
  equipment: 'Equipment',
  items: 'Magic Items',
  bases: 'Bases',
};

const MOUNT_CHUNK = 40;

const ReferenceCard: React.FC<{ entry: FrostgraveReferenceEntry }> = ({ entry }) => {
  const seed = hashStringToSeed(entry.id);
  const surfaceSx = React.useMemo(() => {
    if (entry.kind === 'scroll') return { ...createParchmentSurface(seed), filter: 'drop-shadow(0 3px 6px rgba(40, 20, 5, 0.25))' };
    if (entry.kind === 'potion') return createColouredGlassSurface(seed);
    return createSubtleIceSurface(seed, { rainbowSheen: entry.category === 'items' });
  }, [seed, entry.kind, entry.category]);
  const parchmentLabelSx = React.useMemo(
    () => (entry.kind === 'potion' ? createParchmentLabelSurface(seed) : undefined),
    [seed, entry.kind]
  );

  const content = (
    <>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, ...(entry.kind === 'scroll' ? parchmentUnderlineSx : {}) }}>{entry.name}</Typography>
      {entry.modifiers && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{entry.modifiers}</Typography>}
      {entry.description && <Typography variant="body2" sx={{ mt: 1 }}>{entry.description}</Typography>}
    </>
  );

  if (entry.kind === 'potion') {
    return (
      // Padding (not margin) reserves space for the bottle-neck ::before so grid rows stay aligned.
      <Box sx={{ height: '100%', pt: '34px', boxSizing: 'border-box' }}>
        <Box sx={{ height: '100%', ...surfaceSx }} p={1.5}>
          <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
            <Box sx={{ ...parchmentLabelSx, maxWidth: '70%' }}>{content}</Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (entry.kind === 'scroll') {
    return (
      <Card elevation={0} sx={{ ...surfaceSx }}>
        <CardContent>{content}</CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ ...surfaceSx }}>
      <CardContent>{content}</CardContent>
    </Card>
  );
};

export const IndexTab: React.FC = () => {
  const referenceSpells = useReferenceDataStore((state) => state.spells);
  const referenceCreatures = useReferenceDataStore((state) => state.creatures);
  const referenceWeapons = useReferenceDataStore((state) => state.weapons);
  const referenceArmours = useReferenceDataStore((state) => state.armours);
  const referenceTreasureItems = useReferenceDataStore((state) => state.treasureItems);
  const baseTypes = useReferenceDataStore((state) => state.baseTypes);
  const baseResources = useReferenceDataStore((state) => state.allBaseResources);
  const activeGameSystem = useGameStore((state) => state.activeGameSystem);
  const game = getGameDefinition(activeGameSystem);
  const isFrostgrave = activeGameSystem === 'frostgrave';
  const spells = useMemo(
    () => (isFrostgrave ? referenceSpells : []),
    [isFrostgrave, referenceSpells]
  );
  const creatures = useMemo(
    () => (isFrostgrave ? referenceCreatures : []),
    [isFrostgrave, referenceCreatures]
  );
  const isMinified = useWarbandStore((state) => state.isMinified);
  const [, startTransition] = useTransition();
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState<FrostgraveIndexCategory | 'all'>('all');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [isReady, setIsReady] = useState(false);
  const [visibleCount, setVisibleCount] = useState(MOUNT_CHUNK);

  useEffect(() => {
    let cancelled = false;
    let index = 0;
    const ids = [...spells.map((spell) => spell.id), ...creatures.map((creature) => creature.id)];

    const finish = () => {
      if (!cancelled) startTransition(() => setIsReady(true));
    };
    const processChunk = (deadline: IdleDeadline) => {
      if (cancelled) return;
      while (index < ids.length && deadline.timeRemaining() > 1) {
        createParchmentSurface(hashStringToSeed(ids[index]));
        index++;
      }
      if (index < ids.length) requestIdleCallback(processChunk);
      else finish();
    };

    setIsReady(false);
    if (ids.length === 0) finish();
    else if (typeof requestIdleCallback !== 'undefined') requestIdleCallback(processChunk);
    else {
      ids.forEach((id) => createParchmentSurface(hashStringToSeed(id)));
      finish();
    }

    return () => { cancelled = true; };
  }, [spells, creatures, startTransition]);

  const allEntries = useMemo(() => buildFrostgraveIndexEntries({
    spells,
    creatures,
    weapons: isFrostgrave ? referenceWeapons : [],
    armours: isFrostgrave ? referenceArmours : [],
    treasureItems: isFrostgrave ? referenceTreasureItems : [],
    baseTypes: isFrostgrave ? baseTypes : [],
    baseResources: isFrostgrave ? baseResources : [],
  }), [spells, creatures, isFrostgrave, referenceWeapons, referenceArmours, referenceTreasureItems, baseTypes, baseResources]);
  const entries = useMemo(
    () => filterFrostgraveIndex(allEntries, deferredSearchQuery, category),
    [allEntries, deferredSearchQuery, category]
  );

  const gridChildren = useMemo(() => {
    const children: React.ReactNode[] = [];
    let lastCategory = '';
    let lastGroup = '';
    for (const entry of entries) {
      if (entry.category !== lastCategory) {
        children.push(<IndexSectionHeader
          key={`section-${entry.category}`}
          label={entry.category === 'spells' ? game.abilitiesName : sectionLabels[entry.category]}
        />);
        lastCategory = entry.category;
        lastGroup = '';
      }
      const group = entry.category === 'spells'
        ? entry.spell.school
        : entry.category === 'bestiary'
          ? formatSoldierType(entry.creature.type, true)
          : entry.group ?? '';
      if (group && group !== lastGroup) {
        children.push(<IndexGroupHeader key={`group-${entry.category}-${group}`} label={group} />);
        lastGroup = group;
      }
      if (entry.category === 'spells') {
        children.push(isMinified
          ? <SpellCardMini key={`spell-${entry.id}`} spell={entry.spell} />
          : <SpellCard key={`spell-${entry.id}`} spell={entry.spell} />);
      } else if (entry.category === 'bestiary') {
        children.push(isMinified
          ? <CreatureCardMini key={`creature-${entry.id}`} creature={entry.creature} />
          : <CreatureCard key={`creature-${entry.id}`} creature={entry.creature} />);
      } else {
        children.push(<ReferenceCard key={`${entry.category}-${entry.id}`} entry={entry} />);
      }
    }
    return children;
  }, [entries, isMinified, game.abilitiesName]);

  // Mount cards in idle-time chunks; committing all ~400 at once freezes the main thread.
  useEffect(() => {
    if (!isReady || visibleCount >= gridChildren.length) return;
    const grow = () => startTransition(() => setVisibleCount((count) => count + MOUNT_CHUNK));
    if (typeof requestIdleCallback !== 'undefined') {
      const id = requestIdleCallback(grow);
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(grow, 50);
    return () => clearTimeout(id);
  }, [isReady, visibleCount, gridChildren.length, startTransition]);

  const visibleChildren = visibleCount < gridChildren.length
    ? gridChildren.slice(0, visibleCount)
    : gridChildren;

  const iceSurface = createIceSurface(10, { icicles: true, card: true });

  return (
    <IndexLayout
      query={searchQuery}
      onQueryChange={setSearchQuery}
      searchPlaceholder={isFrostgrave ? 'Search spells, creatures, equipment, items and bases…' : 'Search powers…'}
      categories={categories}
      category={category}
      onCategoryChange={setCategory}
      loading={!isReady}
      isEmpty={entries.length === 0}
      emptyMessage={isFrostgrave
        ? 'No spells or creatures match your search.'
        : 'No powers match your search.'}
      headerSx={{ ...iceSurface, position: 'sticky', top: 0, zIndex: 10, p: 1.5 }}
      contentSx={{ ...iceSurface, p: 1.5 }}
      gridSx={{
        ...(isMinified ? miniFlexSx : gridFlexSx),
        gap: 3,
        // Skip layout/paint for off-screen cards.
        '& > *': { contentVisibility: 'auto', containIntrinsicSize: 'auto 180px' },
      }}
    >
      {visibleChildren}
    </IndexLayout>
  );
};
